import React from 'react';
import { formatPriceAmountToString } from 'libs/end-to-end/src/currencies';
import { MinusCircleIcon } from '@heroicons/react/20/solid';
import cn from 'classnames';
import { Heading } from 'apps/sigma2-ui/src/components/ui/Heading';
import { Paragraph } from 'apps/sigma2-ui/src/components/ui/Paragraph';
import { IconAddCircleOutlineSvg } from 'apps/sigma2-ui/src/components/svg/IconAddCircleOutline';
import { DropdownChevronUpSvg } from 'apps/sigma2-ui/src/components/svg/DropdownChevronUp';
import { DropdownChevronDownSvg } from 'apps/sigma2-ui/src/components/svg/DropdownChevronDown';
import { IconMagnifierSearchSvg } from 'apps/sigma2-ui/src/components/svg/IconMagnifierSearch';
import { IconShieldCheckSvg } from 'apps/sigma2-ui/src/components/svg/IconShieldCheck';
import { PayBenchmarkingCompensationGraphView } from 'apps/sigma2-ui/src/pay-benchmarking/components/PayBenchmarkingCompensationGraphView';
import { SlatePoolSubject } from 'libs/end-to-end/src/copilot';
import { PoolSubject } from 'libs/end-to-end/src/copilot';
import { useLanguage } from 'apps/sigma2-ui/src/lib/hooks/useLanguage';
import { SlateMetric } from 'apps/sigma2-ui/src/copilot/components/SlateMetric';

interface SlateSubjectProps {
  subject: SlatePoolSubject;
  selectedSubjectId: string | null;
  setSelectedSubjectId: (id: string) => void;
  removeFromSlate: (subject: PoolSubject) => void;
  addToSlate: (subject: PoolSubject) => void;
  focusArea: string;
}

export const SlateSubject = (props: SlateSubjectProps) => {
  const {
    subject,
    selectedSubjectId,
    setSelectedSubjectId,
    removeFromSlate,
    addToSlate,
    focusArea,
  } = props;
  const {
    id,
    confidenceLevel,
    jobTitle,
    isInSlate,
    readinessRating,
    compensationFigures,
    education,
    location,
    experienceInYears,
  } = subject;

  const [open, setOpen] = React.useState(false);

  const language = useLanguage();

  const confidenceTextMapping = {
    low: language.statisticalConfidenceLow(),
    medium: language.statisticalConfidenceMedium(),
    high: language.statisticalConfidenceHigh(),
  };

  const hasLowConfidence = confidenceLevel === 'low';
  const confidenceText = confidenceTextMapping[confidenceLevel];

  return (
    <div
      id={`copilotSlateSubjectView${id}`}
      key={id}
      className="pb-1 last:pb-0 pl-4 pr-8"
    >
      <div
        className={cn(' bg-white rounded-lg shadow-12px pt-3 border-2', {
          'border-tertiary': id === selectedSubjectId,
          'pb-3': id !== selectedSubjectId,
        })}
        onClick={() => setSelectedSubjectId(id)}
      >
        <div
          className={cn('flex flex-col gap-1 pl-6', {
            'pb-3': id === selectedSubjectId,
          })}
        >
          <div className="grow pr-4">
            <div className="flex justify-between">
              <div>
                <Paragraph
                  textColor="text-black"
                  className="text-base capitalize text-black"
                >
                  {jobTitle}
                </Paragraph>
                <Paragraph className="text-xs" textColor="text-gray-600">
                  {language.hiringCopilotSlateItemID({
                    id: id,
                  })}
                </Paragraph>
              </div>

              <div className="grow-0">
                {isInSlate ? (
                  <button
                    data-qa="removeFromSlateButton"
                    onClick={() => {
                      removeFromSlate(subject);
                    }}
                    className=" bg-white"
                  >
                    <MinusCircleIcon className="w-6 h-6 text-tertiary" />
                  </button>
                ) : (
                  <button
                    data-qa="addToSlateButton"
                    onClick={() => {
                      addToSlate(subject);
                    }}
                    className=" bg-white"
                  >
                    <IconAddCircleOutlineSvg
                      svg={{
                        className: 'text-tertiary',
                        height: 24,
                        width: 24,
                      }}
                    />
                  </button>
                )}
              </div>
            </div>
          </div>

          <div className="grow pr-4">
            <div className="justify-between flex">
              <div className="text-xs grow-0 flex items-end">
                <Paragraph className="pr-1">
                  {language.hiringCopilotSlateItemReadiness()}
                </Paragraph>
                <Paragraph
                  textColor={
                    readinessRating >= 80 ? 'text-green-600' : 'text-black'
                  }
                  className="text-sm"
                >
                  {readinessRating}%
                </Paragraph>
              </div>

              {focusArea === 'Hiring' && compensationFigures && (
                <div className="text-xs grow-0 flex items-end">
                  <Paragraph className="pr-1">
                    {language.hiringCopilotSlateItemOffer()}
                  </Paragraph>
                  <Paragraph textColor="text-black" className="text-sm">
                    {formatPriceAmountToString({
                      amount: compensationFigures.suggestedTotalCompensation,
                      currency: 'USD',
                    })}
                    /yr
                  </Paragraph>
                </div>
              )}
            </div>
          </div>
        </div>

        {id === selectedSubjectId && (
          <div className="flex flex-col gap-2 px-6 py-3 bg-[#FAFAFA] rounded-b-md">
            <div
              className="flex flex-row justify-between items-center cursor-pointer"
              onClick={() => {
                setOpen(!open);
              }}
            >
              <Paragraph textColor="text-gray-600" className="font-bold">
                {compensationFigures ? 'Suggested Compensation' : 'More Info'}
              </Paragraph>
              {open ? (
                <DropdownChevronUpSvg
                  svg={{
                    className: 'w-4 h-[17px] block text-secondary',
                  }}
                />
              ) : (
                <DropdownChevronDownSvg
                  svg={{
                    className: 'w-4 h-[17px] block text-secondary',
                  }}
                />
              )}
            </div>

            {open && (
              <div>
                {compensationFigures && (
                  <div>
                    <Heading
                      level={4}
                      textColor={'text-black'}
                      className="pb-4"
                    >
                      {formatPriceAmountToString({
                        amount: compensationFigures.suggestedTotalCompensation,
                        currency: 'USD',
                      })}
                      /yr
                    </Heading>
                    <div className="flex flex-row gap-2 items-center pb-2">
                      <Paragraph textColor={'text-black'}>
                        Base:{' '}
                        {formatPriceAmountToString({
                          amount: compensationFigures.suggestedBaseSalary,
                          currency: 'USD',
                        })}
                        /yr
                      </Paragraph>
                      <Paragraph textColor={'text-black'}>
                        Variable:{' '}
                        {formatPriceAmountToString({
                          amount: compensationFigures.suggestedVariablePay,
                          currency: 'USD',
                        })}
                        /yr
                      </Paragraph>
                    </div>
                    <PayBenchmarkingCompensationGraphView
                      currency={'USD'}
                      compensationFigures={compensationFigures}
                      variant={'Simple'}
                      className={'h-[2.5rem]'}
                    />
                    <div className="flex gap-2 pb-2">
                      <div className="flex items-center gap-2">
                        <div className="h-[0.5rem] w-[1.5rem] rounded-full bg-royal-800 opacity-75" />
                        <Paragraph textColor={'text-black'} className="text-sm">
                          Market range
                        </Paragraph>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="h-[0.5rem] w-[1.5rem] rounded-full bg-violet-900 opacity-75" />
                        <Paragraph textColor={'text-black'} className="text-sm">
                          Internal band
                        </Paragraph>
                      </div>
                    </div>
                  </div>
                )}

                <div className="flex flex-row gap-2 items-center pb-2">
                  {hasLowConfidence ? (
                    <div className="rounded-lg bg-[#DBDBDB4D]">
                      <IconMagnifierSearchSvg
                        svg={{
                          className: 'w-4 h-4 text-gray-600',
                        }}
                      />
                    </div>
                  ) : (
                    <div className="rounded-lg bg-[#A6ED8E4D]">
                      <IconShieldCheckSvg
                        svg={{
                          className: 'w-4 h-4 text-green-600',
                        }}
                      />
                    </div>
                  )}
                  <Paragraph
                    textColor="text-black"
                    className="text-sm text-black font-bold"
                  >
                    {confidenceText}
                  </Paragraph>
                </div>

                <SlateMetric
                  metric={Boolean(education)}
                  tooltip={language.copilotTooltipEducation()}
                  value={education ?? language.degreeUnknown()}
                />

                <SlateMetric
                  metric={
                    experienceInYears !== null &&
                    experienceInYears !== undefined
                  }
                  tooltip={language.copilotTooltipExperience()}
                  value={experienceInYears ? `${experienceInYears} yrs` : 'N/A'}
                />

                <SlateMetric
                  metric={Boolean(location)}
                  tooltip={language.copilotTooltipLocation()}
                  value={location ?? language.copilotTooltipNotEnoughData()}
                />
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};
